/**
 * Provider Payout Desk
 * History, period statement (paid/unpaid), email/download, settle shortcuts
 */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Chip,
  TextField,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from "@mui/material";
import {
  AccountBalance,
  History,
  ArrowBack,
  PictureAsPdf,
  Email,
  Download,
} from "@mui/icons-material";
import {
  PageShell,
} from "@/shared/components/pageShell";
import {
  getProviderPayoutSummary,
  getProviderTransactions,
  getProviderStatement,
  downloadProviderStatementPdf,
  emailProviderStatement,
  getRunDeliveryOptions,
} from "../services/payoutService.js";
import { getPayeeOptions } from "../../finance/services/payeeOptionsService.js";
import { logError } from "@/shared/utils/logger.js";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "R0.00";
  return `R${Number(amount).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-ZA", {
      dateStyle: "medium",
    });
  } catch {
    return dateString;
  }
};

const statusColor = (status) => {
  switch (status) {
    case "settled":
      return "success";
    case "payment_initiated":
    case "provider_confirmed":
      return "warning";
    case "cancelled":
      return "default";
    default:
      return "info";
  }
};

export default function PayoutDashboardView() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [statement, setStatement] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includePaid, setIncludePaid] = useState(true);
  const [includeUnpaid, setIncludeUnpaid] = useState(true);
  const [emailTo, setEmailTo] = useState("");
  const [canEmail, setCanEmail] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState("");
  const [actionMsg, setActionMsg] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const loadPayeeOptions = async () => {
      try {
        const options = await getPayeeOptions();
        setPayeeOptions(options);
      } catch (err) {
        logError("Failed to load payee options", { error: err.message });
      }
    };
    loadPayeeOptions();
  }, []);

  const loadDesk = useCallback(async () => {
    if (!providerId) {
      setError("Provider ID is required");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [sumRes, txRes, stmtRes] = await Promise.all([
        getProviderPayoutSummary(providerId),
        getProviderTransactions(providerId, {
          from: from || undefined,
          to: to || undefined,
          limit: 50,
        }),
        getProviderStatement(providerId, {
          from: from || undefined,
          to: to || undefined,
          includePaid,
          includeUnpaid,
        }),
      ]);
      if (!sumRes.success) throw new Error(sumRes.error || "Summary failed");
      setSummary(sumRes.data);
      setTransactions(txRes.data?.items || txRes.data?.data?.items || []);
      if (stmtRes.success) {
        setStatement(stmtRes.data?.data || stmtRes.data);
      }

      // Delivery options via last run if present
      const lastId = sumRes.data?.lastPayoutRun?.payoutRunId;
      if (lastId) {
        const del = await getRunDeliveryOptions(lastId);
        if (del.success) {
          const d = del.data?.data || del.data;
          setCanEmail(Boolean(d?.canEmail));
          setDefaultEmail(d?.email || "");
          if (d?.email && !emailTo) setEmailTo(d.email);
        }
      }
    } catch (err) {
      logError("Failed to load provider payout desk", {
        providerId,
        error: err.message,
      });
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [providerId, from, to, includePaid, includeUnpaid]);

  useEffect(() => {
    loadDesk();
  }, [loadDesk]);

  const payeeById = useMemo(() => {
    return new Map((payeeOptions || []).map((opt) => [opt.id, opt]));
  }, [payeeOptions]);

  const getPayeeDisplay = (payeeId) => {
    const opt = payeeById.get(payeeId);
    const name = opt?.meta?.name || null;
    return { name: name || null, id: payeeId || "N/A" };
  };

  const handleDownloadStatement = async () => {
    setBusy(true);
    setActionErr(null);
    setActionMsg(null);
    const result = await downloadProviderStatementPdf(providerId, {
      from: from || undefined,
      to: to || undefined,
      includePaid,
      includeUnpaid,
    });
    setBusy(false);
    if (!result.success) setActionErr(result.error);
    else setActionMsg("Statement PDF downloaded.");
  };

  const handleEmailStatement = async () => {
    setBusy(true);
    setActionErr(null);
    setActionMsg(null);
    const result = await emailProviderStatement(providerId, {
      from: from || undefined,
      periodTo: to || undefined,
      includePaid,
      includeUnpaid,
      toEmail: emailTo || undefined,
    });
    setBusy(false);
    if (!result.success) {
      setActionErr(
        result.code === "EMAIL_REQUIRED"
          ? "No valid email — use Download instead."
          : result.error
      );
    } else {
      setActionMsg("Statement email queued.");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <PageShell sx={{ maxWidth: 1200, mx: "auto" }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/finance")} sx={{ mt: 2 }}>
          Back to Finance
        </Button>
      </PageShell>
    );
  }

  const provider = getPayeeDisplay(providerId);
  const effectiveCanEmail = canEmail || Boolean(emailTo && emailTo.includes("@"));

  return (
    <PageShell sx={{ maxWidth: 1400, mx: "auto" }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate("/finance")}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Provider payout desk
        </Typography>
        <Chip
          icon={<AccountBalance />}
          label={provider.name || provider.id}
          color="primary"
          variant="outlined"
        />
        {provider.name && (
          <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
            {provider.id}
          </Typography>
        )}
      </Box>

      {actionMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionMsg(null)}>
          {actionMsg}
        </Alert>
      )}
      {actionErr && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionErr(null)}>
          {actionErr}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Ledger balance
              </Typography>
              <Typography variant="h5">
                {formatCurrency(summary?.balance?.balance ?? summary?.balance?.net)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Last payout run
              </Typography>
              {summary?.lastPayoutRun ? (
                <>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {summary.lastPayoutRun.payoutRunId}
                  </Typography>
                  <Typography variant="caption">
                    {formatDate(summary.lastPayoutRun.executedAt)} ·{" "}
                    {formatCurrency(summary.lastPayoutRun.totals?.net)}
                  </Typography>
                </>
              ) : (
                <Typography>—</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  startIcon={<History />}
                  onClick={() =>
                    navigate(`/finance/payouts/history?payeeId=${providerId}`)
                  }
                >
                  Full history
                </Button>
                {summary?.lastPayoutRun?.payoutRunId && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      navigate(
                        `/finance/payouts/run/${summary.lastPayoutRun.payoutRunId}`
                      )
                    }
                  >
                    Settle last run
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Period statement
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="From"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includePaid}
                  onChange={(e) => setIncludePaid(e.target.checked)}
                />
              }
              label="Include paid"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeUnpaid}
                  onChange={(e) => setIncludeUnpaid(e.target.checked)}
                />
              }
              label="Include unpaid"
            />
            <Button variant="outlined" onClick={loadDesk} disabled={busy}>
              Refresh
            </Button>
          </Stack>

          {statement && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Paid net {formatCurrency(statement.paid?.totals?.net)} · Unpaid net{" "}
              {formatCurrency(statement.unpaid?.totals?.net)} · Grand{" "}
              {formatCurrency(statement.grandTotals?.net)}
            </Alert>
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label="Email To (override)"
              size="small"
              fullWidth
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              helperText={
                defaultEmail
                  ? `Default: ${defaultEmail}`
                  : "No profile email — Download is primary"
              }
              disabled={!effectiveCanEmail && !emailTo}
            />
            <Button
              variant={effectiveCanEmail ? "contained" : "outlined"}
              startIcon={busy ? <CircularProgress size={16} /> : <Email />}
              onClick={handleEmailStatement}
              disabled={busy || !effectiveCanEmail}
            >
              Email
            </Button>
            <Button
              variant={!effectiveCanEmail ? "contained" : "outlined"}
              startIcon={busy ? <CircularProgress size={16} /> : <Download />}
              onClick={handleDownloadStatement}
              disabled={busy}
            >
              Download statement PDF
            </Button>
          </Stack>
          {!effectiveCanEmail && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Email disabled — no valid provider email. Download the statement and send
              manually.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Transaction history
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Run</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Jobs</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell>POP</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary">No runs found.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((run) => (
                    <TableRow key={run.payoutRunId} hover>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {run.payoutRunId}
                      </TableCell>
                      <TableCell>{formatDate(run.executedAt)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={run.status}
                          color={statusColor(run.status)}
                        />
                      </TableCell>
                      <TableCell>{run.jobCount}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(run.totals?.net)}
                      </TableCell>
                      <TableCell>{run.hasPop ? "Yes" : "—"}</TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<PictureAsPdf />}
                          onClick={() =>
                            navigate(`/finance/payouts/run/${run.payoutRunId}`)
                          }
                        >
                          Settle / detail
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </PageShell>
  );
}
